import type { FC } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import BadgeEnterprisePng2x from './assets/BadgeEnterprise@2x.png'
import BadgeEnterpriseWebp from './assets/BadgeEnterprise.webp'
import BadgeEnterpriseWebp2x from './assets/BadgeEnterprise@2x.webp'

import BadgeMidMarketPng2x from './assets/BadgeMidMarket@2x.png'
import BadgeMidMarketWebp from './assets/BadgeMidMarket.webp'
import BadgeMidMarketWebp2x from './assets/BadgeMidMarket@2x.webp'

import BadgeUnitedKingdomPng2x from './assets/BadgeUnitedKingdom@2x.png'
import BadgeUnitedKingdomWebp from './assets/BadgeUnitedKingdom.webp'
import BadgeUnitedKingdomWebp2x from './assets/BadgeUnitedKingdom@2x.webp'

import BadgeEuropePng2x from './assets/BadgeEurope@2x.png'
import BadgeEuropeWebp from './assets/BadgeEurope.webp'
import BadgeEuropeWebp2x from './assets/BadgeEurope@2x.webp'

const BadgeEnterpriseImage: FC = () => (
  <picture>
    <source type="image/webp" srcSet={`${BadgeEnterpriseWebp}, ${BadgeEnterpriseWebp2x} 2x`} />
    <Image src={BadgeEnterprisePng2x} alt="G2 badge: Enterprise Leader" width="220" height="260" loading="lazy" />
  </picture>
)

const BadgeMidMarketImage: FC = () => (
  <picture>
    <source type="image/webp" srcSet={`${BadgeMidMarketWebp}, ${BadgeMidMarketWebp2x} 2x`} />
    <Image src={BadgeMidMarketPng2x} alt="G2 badge: Mid-Market Leader" width="220" height="260" loading="lazy" />
  </picture>
)

const BadgeUnitedKingdomImage: FC = () => (
  <picture>
    <source type="image/webp" srcSet={`${BadgeUnitedKingdomWebp}, ${BadgeUnitedKingdomWebp2x} 2x`} />
    <Image
      src={BadgeUnitedKingdomPng2x}
      alt="G2 badge: United Kingdom Leader"
      width="220"
      height="260"
      loading="lazy"
    />
  </picture>
)

const BadgeEuropeImage: FC = () => (
  <picture>
    <source type="image/webp" srcSet={`${BadgeEuropeWebp}, ${BadgeEuropeWebp2x} 2x`} />
    <Image src={BadgeEuropePng2x} alt="G2 badge: Europe Leader" width="220" height="260" loading="lazy" />
  </picture>
)

const data = [
  {
    image: <BadgeEnterpriseImage />,
    title: `Enterprise Leader`,
  },
  {
    image: <BadgeMidMarketImage />,
    title: `Mid-Market Leader`,
  },
  {
    image: <BadgeUnitedKingdomImage />,
    title: `United Kingdom Leader`,
  },
  {
    image: <BadgeEuropeImage />,
    title: `Europe Leader`,
  },
]

const Badges: FC = () => {
  return (
    <StyledSectionBase variant="noCopy">
      <Section.Contents>
        <StyledColumnTrack
          columns={2}
          overrides={{
            md: 4,
          }}
        >
          {data.map(({ image, title }, index) => (
            <Badge key={index}>
              <StyledTitle>{title}</StyledTitle>
              {image}
            </Badge>
          ))}
        </StyledColumnTrack>
      </Section.Contents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 24px;

    ${({ theme }) => theme.breakpoints.up(`sm`)} {
      padding-top: 40px;
    }
  }
`

const StyledTitle = styled.p`
  ${({ theme }) => theme.typography.textBoldSmall}
  color: ${({ theme }) => theme.palette.twilio.gray[100]};

  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    ${({ theme }) => theme.typography.h5}
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  gap: 24px;
  max-width: 1024px;
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    gap: 48px;
  }
`

const Badge = styled.div`
  text-align: center;
`

export default Badges
